import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { client } from "../../api/client"

const apiURL = process.env.REACT_APP_QUERY_API_URL
const recordCount = parseInt(process.env.REACT_APP_RECORDCOUNT) || 100;

const initialState = {
    logs: [],
    status: 'idle',
    error: null,
    lastkey: {},
    logParams: {},
    searchId: 0,
}

const QueryParamaters = {
    DateFrom: '',
    DateTo: '',
    UserIds: [],
}

export const fetchLogs = createAsyncThunk(
    'logs/fetchLogs',
    async (paramaters) => {
        try {
            //console.log('search', paramaters)           
            if (process.env.REACT_APP_USELOCALDATA === "true") {
                return require('./_local/_auditlogs.json')
            }
            const response = await client.post(apiURL, '?type=auditlogs', paramaters)
            //console.log('logs/fetchLogs', response.data)
            
            return response.data    
        } catch (error) {
            //console.log('logs/fetchLogs', error)
            throw error
        }
    })

export const fetchMoreLogs = createAsyncThunk(
    'logs/fetchMoreLogs',
    async (paramaters) => {
        try {
            //console.log('search', paramaters)
            if (process.env.REACT_APP_USELOCALDATA === "true") {
                return require('./_local/_auditlogs.json')
            }
            const response = await client.post(apiURL, '?type=auditlogs', paramaters)
            //console.log('fetchMoreLogs', response.data)

            return response.data
        } catch (error) {
            //console.log('logs/fetchMoreLogs', error)
            throw error
        }

    })

export const fetchLogsBatch = createAsyncThunk(
    'logs/fetchLogsBatch',
    /**
     * initialParams = {
     *   // all your usual search params (DateFrom, UserIds…)
     *   LastEvaluatedKey: {},    // empty for first page, or carryover for next
     *   append: boolean,         // false = replace array, true = append
     *   mode: 'initial' or 'more', // 'initial' for new search, 'more' for pagination
     *   SearchId: number
     * }
     */
    async (initialParams, { dispatch, rejectWithValue }) => {
        try {
            const { SearchId, mode, ...params } = initialParams;
            const isInitial = mode === 'initial';
            const isMore = mode === 'more';

            if (isInitial) {
                // For initial, we will fetch multiple batches and append incrementally
                let allLogs = [];
                let effectiveLastKey = null;
                let currentParams = { ...params };

                while (allLogs.length < recordCount) {
                    const resp = await client.post(apiURL, '?type=auditlogs', currentParams);
                    const { Results = [], LastEvaluatedKey } = resp.data;

                    if (Results.length > 0) {
                        // Dispatch appendLogs to update UI immediately
                        dispatch(appendLogs({ results: Results, lastkey: LastEvaluatedKey || {}, SearchId }));
                        allLogs = allLogs.concat(Results);
                    }

                    if (LastEvaluatedKey && Object.keys(LastEvaluatedKey).length > 0) {
                        currentParams.LastEvaluatedKey = LastEvaluatedKey;
                        effectiveLastKey = LastEvaluatedKey;
                    } else {
                        effectiveLastKey = null;
                        break;
                    }

                    if (allLogs.length >= recordCount) {
                        break;
                    }
                }

                return {
                    Results: [], // Not used for initial
                    LastEvaluatedKey: effectiveLastKey || {},
                    append: false,
                    SearchId,
                    mode
                };
            } else if (isMore) {
                // For more, fetch multiple batches until we have recordCount new results
                let allResults = [];
                let effectiveLastKey = null;
                let currentParams = { ...params };

                while (allResults.length < recordCount) {
                    const resp = await client.post(apiURL, '?type=auditlogs', currentParams);
                    const { Results = [], LastEvaluatedKey } = resp.data;

                    if (Results.length > 0) {
                        // Dispatch appendLogs to update UI immediately
                        dispatch(appendLogs({ results: Results, lastkey: LastEvaluatedKey || {}, SearchId }));
                        allResults = allResults.concat(Results);
                    }

                    if (LastEvaluatedKey && Object.keys(LastEvaluatedKey).length > 0) {
                        currentParams.LastEvaluatedKey = LastEvaluatedKey;
                        effectiveLastKey = LastEvaluatedKey;
                    } else {
                        effectiveLastKey = null;
                        break;
                    }

                    if (allResults.length >= recordCount) {
                        break;
                    }
                }

                return {
                    Results: [],
                    LastEvaluatedKey: effectiveLastKey || {},
                    append: true,
                    SearchId,
                    mode
                };
            } else {
                throw new Error('Invalid mode');
            }

        }
        catch (err) {
            console.error('logs/fetchLogsBatch: ', err)
            return rejectWithValue(err.message)
        }
    }
)

const logsSlice = createSlice({
    name: 'logs',
    initialState,
    reducers: {
        clearData(state, action) {
            state.logs = []
            state.logParams = {}
        },
        setLogParams(state, action) {
            state.logParams = action.payload
            state.searchId = action.payload.SearchId
            //console.log('setLogParams', state.logParams)
        },
        updateLogParamsLastKey(state, action) {
            state.logParams.LastEvaluatedKey = action.payload
            //console.log('updateLogParamsLastKey', state.logParams)
        },
        appendLogs(state, action) {
            if (action.payload.SearchId === state.searchId) {
                state.logs = state.logs.concat(action.payload.results);
                state.lastkey = action.payload.lastkey;
            }
        }
    },
    extraReducers(builder) {
        builder
        //get logs
        .addCase(fetchLogs.pending, (state, action) => {
            state.status = 'loading'
        })
        .addCase(fetchLogs.fulfilled, (state, action) => {
            state.status = 'succeeded'
            state.logParams.LastEvaluatedKey = action.payload.LastEvaluatedKey
            //set fetched logs
            state.logs = action.payload.Results
            //console.log('fetchLogs.fulfilled', state.logs)
        })
        .addCase(fetchLogs.rejected, (state, action) => {
            state.status = 'failed'
            //console.log('fetchLogs.rejected error: ', action.error.message)
            state.error = action.error.message
        })

        //get more logs using LastEvalutatedKey
        .addCase(fetchMoreLogs.pending, (state, action) => {
            state.status = 'loading'
        })
        .addCase(fetchMoreLogs.fulfilled, (state, action) => {
            state.status = 'succeeded'
            //console.log('fetchMoreLogs.fulfilled', action.payload)
            state.logParams.LastEvaluatedKey = action.payload.LastEvaluatedKey
            //set fetched logs
            state.logs = state.logs.concat(action.payload.Results)
            //console.log('fetchMoreLogs.fulfilled', state.logs)
        })
        .addCase(fetchMoreLogs.rejected, (state, action) => {
            state.status = 'failed'
            //console.log('fetchMoreLogs.rejected error: ', action.error.message)
            state.error = action.error.message
        })

        //get logs batch
        .addCase(fetchLogsBatch.pending, (state, action) => {
            const mode = action.meta.arg.mode;
            if (mode === 'more') {
                state.status = 'fetchingMore';
            } else {
                state.status = 'loading';
            }
        })
        .addCase(fetchLogsBatch.fulfilled, (state, { payload }) => {
            if (payload.SearchId === state.searchId) {
                state.status = 'succeeded';
                // For both initial and more, logs are already appended via dispatch(appendLogs)
                // Update lastkey from the final payload so pagination can continue
                state.lastkey = payload.LastEvaluatedKey;
            }
        })
        .addCase(fetchLogsBatch.rejected, (state, { payload, error }) => {
            state.status = 'failed'
            state.error = payload || error.message
            console.error('❌ Batch failed:', payload || error.message)
        })
    }
})

export const { clearData, setLogParams, updateLogParamsLastKey, appendLogs } = logsSlice.actions

export const selectLogs = (state) => state.logs.logs
export const selectLogParams = (state) => state.logs.logParams
export const selectLastEvaluatedKey = (state) => state.logs.lastkey
export const selectLogsError = (state) => state.logs.error

export default logsSlice.reducer